import {
  AccountTypeEnum,
  ComplianceCaseTypeEnum,
  DocumentStatus,
} from "#graphql/supergraphEnums";
import {
  type ComplianceCaseTypeEnum as ComplianceCaseType,
  AccountTypeEnum as AccountType,
} from "#graphql/supergraphTypes";
import {
  isIndividualAccountType,
  isJoinAccountType,
  resolvedAccountType,
} from "#utils/account_types";
import { Programs } from "#utils/compliance_program";
import {
  getProfileCompletionStatus,
  getProfileDisplayStatus,
} from "../common/ProfileManagement/profile_status_lookup";
import {
  CompletionDocumentStatus,
  type DocumentForStatus,
  ProfileCategory,
  ProfileTypeForStatus,
  type RequestedDocumentTypeForStatus,
  RootProfileConfig,
} from "../common/ProfileManagement/types";
import { ProfileStatus } from "../ProfileStatusBadge/types";

export enum NavigationMode {
  IraCategorization = "ira_categorization",
  AccountTypeSelection = "account_type_selection",
  BasePath = "base_path",
}

const prepareProfileParams = (
  rootProfile: RootProfileConfig,
  profile?: ProfileTypeForStatus | null,
) => {
  const isRoot = profile?.id === rootProfile.id;
  const rootAccountType = resolvedAccountType(rootProfile);
  const profileAccountType = resolvedAccountType(profile);

  const computeProfileCategory = () => {
    const isRootJointAccount = isJoinAccountType(rootAccountType);
    const isSelfJointAccount = isJoinAccountType(profileAccountType);
    const isIndividual = isIndividualAccountType(profileAccountType);
    if (isIndividual) {
      if (isRootJointAccount) {
        return ProfileCategory.JOINT_INDIVIDUAL;
      } else {
        return ProfileCategory.INDIVIDUAL;
      }
    } else {
      if (isSelfJointAccount) {
        return ProfileCategory.JOINT;
      } else {
        return ProfileCategory.ENTITY;
      }
    }
  };

  const nonDefaultQuestions =
    rootProfile?.amlEntityTypeQuestions?.filter((question) => !question.isDefault) || [];
  // If there is only one (default) question, no reason to show the survey. The aml_entity_type will already be set.
  const showSurvey = nonDefaultQuestions?.length > 0;
  return {
    isRoot: isRoot,
    profileCategory: computeProfileCategory(),
    surveyAvailable: showSurvey,
    rootAccountType,
    profileAccountType,
  };
};

const getNavigationMode = (
  caseType: ComplianceCaseType,
  isRoot: boolean,
  accountType?: AccountType | null,
  skipDetailsForRootProfile?: boolean,
) => {
  if (isRoot) {
    if (accountType === AccountTypeEnum.Ira) {
      return NavigationMode.IraCategorization;
    } else if (skipDetailsForRootProfile || isIndividualAccountType(accountType)) {
      return null;
    } else if (caseType === ComplianceCaseTypeEnum.DocumentRefresh) {
      // For document refresh we will disallow account selection for the root investor
      return null;
    } else {
      // Joint / Entity accounts
      return NavigationMode.AccountTypeSelection;
    }
  } else {
    return NavigationMode.BasePath;
  }
};

export const shouldShow = (
  caseType: ComplianceCaseType,
  rootProfile: RootProfileConfig,
  profile?: ProfileTypeForStatus | null,
  programId?: number,
  skipDetailsForRootProfile?: boolean,
) => {
  const { isRoot, profileCategory, surveyAvailable, profileAccountType } = prepareProfileParams(
    rootProfile,
    profile,
  );
  const profileDisplayStatus = getProfileDisplayStatus(
    caseType,
    programId || Programs.KYC,
    profileCategory,
    isRoot,
    surveyAvailable,
    profile
      ? {
          id: profile.id,
          complianceObjectType: profile.complianceObjectType,
          accountType: profileAccountType,
          dateOfBirth: profile.dateOfBirth,
          legalName: profile.legalName,
          amlEntityTypeId: profile.amlEntityTypeId,
          parentRelationships: profile.parentRelationships || [],
          caseDataEntryStatus: profile.caseDataEntryStatus,
          rulesEngine: profile.rulesEngine || { documentTypes: [] },
        }
      : null,
  );
  return {
    navigationMode: getNavigationMode(
      caseType,
      isRoot,
      profileAccountType,
      skipDetailsForRootProfile,
    ),
    showDetails: profileDisplayStatus.detailsAvailability,
    showDocuments: profileDisplayStatus.documentsAvailability,
    showOwners: profileDisplayStatus.ownerAvailability,
    showControllers: profileDisplayStatus.controllerAvailability,
    showAsIdentificationCard:
      !skipDetailsForRootProfile && isRoot && isIndividualAccountType(profileAccountType),
  };
};

// Special handling for joint-root trees:
// child profiles are never joint, so we use the root profile's requested document
// types and derive upload status from each child's actual documents.
const deriveDocumentStatusForJointProfile = (
  rootDocumentTypes: RequestedDocumentTypeForStatus[],
  profileDocuments: DocumentForStatus[],
): RequestedDocumentTypeForStatus[] => {
  return rootDocumentTypes.map((docType) => {
    const hasMatchingDocument = profileDocuments.some((doc) => {
      if (!doc.documentPurpose) return false;
      if (doc.documentPurpose.baseType !== docType.baseType) return false;
      if (docType.subType) {
        const typeMetadata = doc.metadata?.find((meta) => meta.key === "type");
        return typeMetadata?.value === docType.subType;
      }
      return true;
    });

    return {
      ...docType,
      status: hasMatchingDocument ? DocumentStatus.Uploaded : DocumentStatus.New,
    };
  });
};

export const completionStatus = (
  caseType: ComplianceCaseType,
  rootProfile: RootProfileConfig,
  profile?: ProfileTypeForStatus | null,
  programId?: number,
) => {
  const { isRoot, profileCategory, rootAccountType, profileAccountType } = prepareProfileParams(
    rootProfile,
    profile,
  );

  const requestedDocTypes = isJoinAccountType(rootAccountType)
    ? deriveDocumentStatusForJointProfile(
        rootProfile?.rulesEngine?.documentTypes || [],
        profile?.documents || [],
      )
    : profile?.rulesEngine?.documentTypes;
  const profileCompletionStatus = getProfileCompletionStatus(
    caseType,
    programId || Programs.KYC,
    profileCategory,
    isRoot,
    requestedDocTypes || [],
    profile
      ? {
          id: profile.id,
          complianceObjectType: profile.complianceObjectType,
          accountType: profileAccountType,
          dateOfBirth: profile.dateOfBirth,
          legalName: profile.legalName,
          amlEntityTypeId: profile.amlEntityTypeId,
          parentRelationships: profile.parentRelationships || [],
          caseDataEntryStatus: profile.caseDataEntryStatus,
          documents: profile.documents || [],
          childOwnersCount: profile.childOwnersCount,
        }
      : null,
  );
  return {
    detailsCompleted: profileCompletionStatus.detailsCompletion,
    documentsCompletionStatus: profileCompletionStatus.documentsCompletionStatus,
    ownersCompleted: profileCompletionStatus.ownersCompletionStatus,
    controllersCompleted: profileCompletionStatus.controllersCompletionStatus,
  };
};

export type ProfileCompletionSnapshot = ReturnType<typeof completionStatus>;

export const isProfileCompleted = (
  caseType: ComplianceCaseType,
  rootProfile: RootProfileConfig,
  profile?: ProfileTypeForStatus | null,
  programId?: number,
  completionSnapshot?: ProfileCompletionSnapshot | null,
) => {
  const { detailsCompleted, documentsCompletionStatus, ownersCompleted, controllersCompleted } =
    completionSnapshot ?? completionStatus(caseType, rootProfile, profile, programId);

  return (
    (detailsCompleted == null || detailsCompleted) &&
    (ownersCompleted == null || ownersCompleted) &&
    (controllersCompleted == null || controllersCompleted) &&
    (documentsCompletionStatus == null ||
      documentsCompletionStatus == CompletionDocumentStatus.COMPLETED ||
      (caseType == ComplianceCaseTypeEnum.InvestorOnboarding &&
        documentsCompletionStatus == CompletionDocumentStatus.ACKNOWLEDGED))
  );
};

export const detailItemStatus = (detailsCompleted: boolean | null): ProfileStatus => {
  return detailsCompleted ? ProfileStatus.Completed : ProfileStatus.NotStarted;
};

export const documentItemStatus = (
  detailsCompleted: boolean | null,
  documentsCompletionStatus: CompletionDocumentStatus | null,
): ProfileStatus => {
  if (detailsCompleted === false) {
    return ProfileStatus.DetailsPending;
  }

  switch (documentsCompletionStatus) {
    case CompletionDocumentStatus.COMPLETED:
      return ProfileStatus.Completed;
    case CompletionDocumentStatus.ACKNOWLEDGED:
    case CompletionDocumentStatus.EXPIRED:
      return ProfileStatus.UpdateRequired;
    default:
      return ProfileStatus.NotStarted;
  }
};

export const ownerItemStatus = (
  ownersCompleted: boolean | null,
  detailsCompleted: boolean | null,
  profileAccountType: AccountType | undefined,
  childOwnersCount?: number,
): ProfileStatus => {
  if (isJoinAccountType(profileAccountType)) {
    const count = childOwnersCount ?? 0;
    if (count < 1) {
      return ProfileStatus.NotStarted;
    }
    if (count < 2) {
      return ProfileStatus.UpdateRequired;
    }
    if (detailsCompleted === false) {
      return ProfileStatus.DetailsPending;
    }
  }
  return ownersCompleted ? ProfileStatus.Completed : ProfileStatus.NotStarted;
};

export const controllerItemStatus = (controllersCompleted: boolean | null): ProfileStatus => {
  return controllersCompleted ? ProfileStatus.Completed : ProfileStatus.NotStarted;
};
